import React, { useEffect, useRef, useState } from 'react';
import Modal from '@tencent/dui/lib/components/Modal';
import i18n from '@i18next/i18n';
import PageRange from '../range';
import FontInfo from './font-info';
import { transaction } from '@src/js/utils';
import { showVipLinktext } from '@src/js/common/ark-extensions/dc-components/vip-linktext';
import { DialogProps, QUICK_TOOL_AID_FEATURE } from '..';
import Loading from '@tencent/dui/lib/components/Loading';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { handleModelMenuEvent } from '@src/slide/application/service/menu-manager/menu-event';
import { ActionType } from '@src/framework/editor/src/js/constants/common-action-type';
import { MenuType } from '@src/slide/domain/ui-model/constant/menu';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import { IShapeBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { TaskPriority } from '@src/slide/infrastructure/module/dependent/taskManager';
import { isTitleShape } from './data-utils';
import { FontFamilyInfo } from '@src/slide/application/service/quick-tool-dialog-manager/core/quick-tool-dialog-manager.interface';
import { PPTEditorStateEnumType } from '../../../edit-extension/edit-extension-export';

const apprec = 'unifyfont';

/**
 * 对model应用字体
 * @param model 页内元素
 * @param fontFamilyInfo 字体信息（中西文/标题正文，设置传字体id，不设置传undefined）
 */
// eslint-disable-next-line complexity
const applyModelFont = (model: IBaseModel, fontFamilyInfo: FontFamilyInfo) => {
    if (model.type === UIModelType.GROUP) {
        model.children.forEach((child) => {
            applyModelFont(child, fontFamilyInfo);
        });
        return;
    }
    const modelIsTitle = (model as IShapeBaseModel).isTitleOrCtrTitlePl
        || isTitleShape(model as IShapeBaseModel);
    const applyTittle = fontFamilyInfo?.titleFontEa || fontFamilyInfo?.titleFontLatin;
    const applyContent = fontFamilyInfo?.contentFontEa || fontFamilyInfo?.contentFontLatin;
    // 当前元素类型与要应用的类型不对应，则忽略
    if ((modelIsTitle && !applyTittle) || (!modelIsTitle && !applyContent)) {
        return;
    }
    handleModelMenuEvent(model, {
        actionType: ActionType.UNIFORM_FONT,
        values: {
            ea: modelIsTitle ? fontFamilyInfo?.titleFontEa : fontFamilyInfo?.contentFontEa,
            latin: modelIsTitle ? fontFamilyInfo?.titleFontLatin : fontFamilyInfo?.contentFontLatin,
        },
        menuType: MenuType.TOOLBAR,
    });
};

/**
 * 对slide数组应用字体
 * @param slides slide数组
 * @param fontFamilyInfo 字体信息（中西文/标题正文，设置传字体id，不设置传undefined）
 */
export const applySlidesFont = async (slides: ISlideModel[], fontFamilyInfo: FontFamilyInfo) => {
    // 开始转换
    const loading = Loading.show({ tip: i18n.t('加载中...') });
    try {
        // 确保loading显示出来
        await new Promise(resolve => setTimeout(resolve, 0));
        // 确保所有页都转换完成
        await Promise.all(slides.map(async (slide) => {
            !slide?.childrenConverted && await slide?.startConvertChildrenTask(TaskPriority.HIGH);
        }));
        // 找到对应的元素，挨个设置字体
        transaction(() => {
            slides.forEach((slide) => {
                slide?.children.forEach((element) => {
                    applyModelFont(element, fontFamilyInfo);
                });
            });
        });
        loading.close();
    } catch (err) {
        loading.close();
        throw err;
    }
};

type IUniformFontDialog = React.FC<DialogProps>;
const UniformFontDialog: IUniformFontDialog = (props: DialogProps) => {
    const {
        visible, onClose, onCancel, onOk, position,
        FontListModule, services, QuickToolFontList, fontFamilyInfo: propsFontFamilyInfo,
    } = props;

    const [slideList, setSlideList] = useState<ISlideModel[]>();
    const [fontFamilyInfo, setFontFamilyInfo] = useState<FontFamilyInfo>();

    const shouldOkDisabled = () => {
        if (!fontFamilyInfo) {
            return true;
        }
        const { titleFontEa, titleFontLatin, contentFontEa, contentFontLatin } = fontFamilyInfo;
        return !titleFontEa && !titleFontLatin && !contentFontEa && !contentFontLatin;
    };

    const handleOk = async () => {
        if (services.userPrivilegeService.pptEditorState !== PPTEditorStateEnumType.EDITABLE) {
            services.tipsManager.showCannotEditTips();
            return;
        }
        if (!onOk(apprec, i18n.t('统一字体')) || !fontFamilyInfo || !slideList) {
            return;
        }
        await applySlidesFont(slideList, fontFamilyInfo).catch(() => {});
        onClose();
    };
    const handleCancel = () => {
        onCancel();
        onClose();
    };

    const vipLinkRef = useRef(null);
    useEffect(() => {
        showVipLinktext({
            ref: vipLinkRef.current,
            name: i18n.t('统一字体'),
            feature: QUICK_TOOL_AID_FEATURE,
            position,
            apprec,
            anchor: 'PPT-TOOLS',
        });
    }, [vipLinkRef.current]);

    return (
        <Modal
            title={i18n.t('统一字体')}
            visible={visible}
            onClose={handleCancel}
            onCancel={handleCancel}
            onOk={handleOk}
            className={'uniform-font-dialog'}
            okDisabled={shouldOkDisabled()}
        >
            <div style={{ width: '100%' }} ref={vipLinkRef}></div>
            <FontInfo
                QuickToolFontList={QuickToolFontList}
                FontListModule={FontListModule}
                fontFamilyInfo={propsFontFamilyInfo}
                onFontChange={(info) => {
                    setFontFamilyInfo(info);
                }}></FontInfo>
            <PageRange
                needGetSelectModels={false}
                onCheckStateChange={list => setSlideList(list)}
                services={{
                    masterLayoutEditService: services.masterLayoutEditService,
                    selectionService: services.selectionService,
                }}>
            </PageRange>
        </Modal>
    );
};

export default UniformFontDialog;
